import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { LoadingButton } from "@mui/lab";
import { useSnackbar } from "notistack";
import moment from "moment";
import _ from "lodash";
import warehouseBinServices from "app/services/warehouse-bin-services";
import printerServices from "app/services/printer-services";
import qzPrintServices from "app/services/qz-print-services";

const PRINTER_KEY = "haulers.warehouse.printer";

// Imprime las etiquetas de posición de rack, mostrando antes cómo va a quedar
// una de ellas.
//
// El preview importa más acá que en las etiquetas de producto: la geometría de
// la etiqueta de rack todavía no está calibrada en hardware, así que conviene
// mirarla antes de mandar a imprimir la bodega entera. El PNG lo renderiza el
// backend con las mismas medidas con que arma el ZPL.
const BinLabelsDialog = ({ open, onClose, centreId }) => {
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState(false);
    const [labels, setLabels] = useState(null);
    const [error, setError] = useState(null);
    const [printers, setPrinters] = useState([]);
    const [printerId, setPrinterId] = useState(null);
    const [qzAvailable, setQzAvailable] = useState(true);
    const [printing, setPrinting] = useState(false);

    useEffect(() => {
        if (!open || !centreId) return;

        setLoading(true);
        setError(null);
        setLabels(null);

        warehouseBinServices
            .getLabels(`?centre_id=${centreId}&preview=true`)
            .then((data) => setLabels(data))
            .catch((err) =>
                setError(err?.response?.data?.error || "No se pudieron generar las etiquetas")
            )
            .finally(() => setLoading(false));
    }, [open, centreId]);

    useEffect(() => {
        if (!open) return;

        Promise.all([
            qzPrintServices.isAvailable(),
            printerServices.getPrinters("?q[active_eq]=true&page=1"),
        ])
            .then(([available, resp]) => {
                setQzAvailable(available);
                const list = resp?.data || [];
                setPrinters(list);
                const last = parseInt(localStorage.getItem(PRINTER_KEY), 10);
                const hit = _.find(list, { id: last }) || list[0] || null;
                setPrinterId(hit?.id || null);
            })
            .catch(() => setPrinters([]));
    }, [open]);

    const printer = useMemo(() => _.find(printers, { id: printerId }), [printers, printerId]);

    const print = async () => {
        if (!labels?.zpl) return;

        try {
            setPrinting(true);
            await qzPrintServices.printZpl({
                printerName: printer?.printer_name,
                host: printer?.ip,
                port: printer?.port,
                zpl: labels.zpl,
            });

            if (printer?.id) localStorage.setItem(PRINTER_KEY, String(printer.id));
            enqueueSnackbar(
                `${labels.count} etiquetas enviadas a ${printer?.name || "la impresora"}`,
                { variant: "success" }
            );
            onClose();
        } catch (err) {
            enqueueSnackbar(err?.message || "No se pudo imprimir", { variant: "error" });
        } finally {
            setPrinting(false);
        }
    };

    // Se mantiene la descarga como salida secundaria: sirve para mandarle el ZPL
    // a alguien o revisarlo a mano mientras la etiqueta no está calibrada.
    const download = () => {
        if (!labels?.zpl) return;

        const blob = new Blob([labels.zpl], { type: "text/plain" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `etiquetas_posiciones_${moment().format("YYYYMMDDHHmmss")}.zpl`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Etiquetas de posición
                {labels && (
                    <Typography variant="body2" color="text.secondary">
                        {labels.count} {labels.count === 1 ? "etiqueta" : "etiquetas"} · vista previa de la primera
                    </Typography>
                )}
            </DialogTitle>
            <DialogContent>
                {loading && (
                    <Stack alignItems="center" sx={{ py: 4 }}>
                        <CircularProgress size={28} />
                    </Stack>
                )}

                {error && <Alert severity="error">{error}</Alert>}

                {labels && (
                    <>
                        {labels.preview ? (
                            <Box
                                sx={{
                                    p: 1,
                                    mb: 2,
                                    bgcolor: "#fff",
                                    border: 1,
                                    borderColor: "divider",
                                    borderRadius: 1,
                                }}
                            >
                                <Box
                                    component="img"
                                    src={labels.preview}
                                    alt="Vista previa de la etiqueta de posición"
                                    sx={{ display: "block", width: "100%" }}
                                />
                            </Box>
                        ) : (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                No se pudo generar la vista previa, pero las etiquetas se pueden imprimir igual.
                            </Alert>
                        )}

                        {!qzAvailable && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                QZ Tray no está corriendo. Ábrelo antes de imprimir.
                            </Alert>
                        )}

                        {printers.length === 0 ? (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                No hay impresoras configuradas. Agrégalas en el mantenedor de Impresoras.
                            </Alert>
                        ) : (
                            <Autocomplete
                                size="small"
                                options={printers}
                                value={printer || null}
                                getOptionLabel={(option) => option.name || ""}
                                onChange={(_event, value) => setPrinterId(value?.id || null)}
                                renderInput={(params) => <TextField {...params} label="Impresora" />}
                                sx={{ mb: 2 }}
                            />
                        )}

                        <Alert severity="warning" sx={{ py: 0.5 }}>
                            <Typography variant="body2">
                                La geometría de estas etiquetas no está calibrada en hardware todavía. Imprimí una
                                tira corta y medila antes de etiquetar la bodega completa.
                            </Typography>
                        </Alert>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button
                    startIcon={<FileDownloadOutlinedIcon />}
                    disabled={!labels?.zpl}
                    onClick={download}
                >
                    Descargar .zpl
                </Button>
                <LoadingButton
                    variant="contained"
                    loading={printing}
                    startIcon={<PrintOutlinedIcon />}
                    disabled={!labels?.zpl || !printer}
                    onClick={print}
                >
                    Imprimir {labels?.count ? `(${labels.count})` : ""}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default BinLabelsDialog;
